import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "../../../axios";
import { notify } from "../../../util/helpers";
import { sendOtpLogic, verifyOtpLogic, resetOtpVerifyToken } from "../../../store/features/app/appSlice";
import "./DeleteAccount.css";
import CardLayout from "../../common/cardLayout/CardLayout";
import AdultLogoDark from "../../../assets/images/Precise_Digital_adult_login_Logo.svg";
import YouthLogoDark from "../../../assets/images/Precise_Digiatl_youth_Login_ Logo.svg";
import { Helmet } from "../../common/Helmet";

const DeleteAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { otpSending, otpVerifying, tempToken, otpError, verifyOtpError } = useSelector((state) => state.app);
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp' | 'done'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const inputs = useRef([]);

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);
    if (value && index < 5) inputs.current[index + 1]?.focus();
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Email and password are required");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await dispatch(sendOtpLogic({ username: email, password })).unwrap();
      setStep('otp');
    } catch (err) {
      setError(err.message || "Failed to send OTP. Check email/password.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpAndDeactivate = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) {
      setError("Please enter full 6-digit OTP");
      return;
    }
    setError("");
    setLoading(true);
    try {
      // Verify OTP first - gets JWT tokens
      const verifyResponse = await dispatch(verifyOtpLogic({ temp_token: tempToken, otp_code: code })).unwrap();
      // Use access token from verify OTP response for deactivate
      const accessToken = verifyResponse.token.access;
      await axios.post("/me/deactivate_account/", { email }, {
        headers: { Authorization: `JWT ${accessToken}` }
      });
      setMessage("Your account has been successfully deactivated. Confirmation email sent.");
      localStorage.clear();
      dispatch({ type: 'app/logoutUser' });
      notify("Account deactivated!", "success");
      setMessage("Account deactivated successfully. Confirmation email sent. Thank you for using Precise Digital. You can close this page now.");
      setStep('done');
    } catch (err) {
      if (err.message?.includes('400')) {
        setError("Invalid OTP. Please check and try again.");
      } else {
        setError(err.message || "OTP verification or deactivation failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const changeCredentials = () => {
    dispatch(resetOtpVerifyToken());
    setStep('credentials');
    setOtp(['', '', '', '', '', '']);
    setEmail("");
    setPassword("");
    setError("");
  };

  useEffect(() => {
    // Stay on success page - no redirect
    if (step === 'done') {
      // Optional: window.location.href = '/login'; for redirect
    }
  }, [step]);

  const currentStepTitle = step === 'credentials' ? 'Verify Credentials' : step === 'otp' ? 'Enter OTP' : 'Complete';

  return (
    <section className="container-fluid deleteAccountBG">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Delete Account | Precise Digital</title>
      </Helmet>
      <div className="col-sm-12 col-lg-10 col-md-10" style={{ margin: "0 auto" }}>
        <div className="row p-5">
          <div className="col-sm-12 col-lg-6 col-md-6">
            <div className="container text-center">
              <img src="/static/media/login-image.47822919fd0c4f56693f.png" alt="Account Management" className="img-fluid" style={{ height: "90vh", borderRadius: "10px" }} />
            </div>
          </div>
          <div className="col-sm-12 col-lg-6 col-md-6">
            <CardLayout className="container col-sm-12 col-md-8 col-lg-8" style={{ marginTop: "5%" }}>
              <div className="text-center container-fluid py-3">
                <img className="pt-3" src={process.env.REACT_APP_DASHBOARD_TYPE === "adult" ? AdultLogoDark : YouthLogoDark} height="80px" width="250px" alt="Precise Digital" />
                <div className="container pt-5">
                  <h4 className="primary-text">Delete Your Account</h4>
                  <span className="secondary-text mb-3">Secure verification required. This cannot be undone.</span>
                  <div className="deletion-process text-start">
                    <strong>Process:</strong>
                    <ol className="mt-2">
                      <li>Enter email & password → Send OTP to email.</li>
                      <li>Enter 6-digit OTP → Account deactivated.</li>
                      <li>Confirmation email sent.</li>
                    </ol>
                  </div>
                  <div className="mt-4">
                    <a href="#developer-section" className="scroll-to-dev">↓ About the developer</a>
                  </div>
                </div>
              </div>
              {message && <div className="alert alert-success text-center py-3 mb-3">{message}</div>}
              {error && <div className="alert alert-danger text-center py-3 mb-3">{error}</div>}
              
              {step === 'credentials' && (
                <form className="container" onSubmit={sendOtp}>
                  <div className="form-group pb-3">
                    <label className="form-label"><strong>Email Address</strong></label>
                    <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required />
                  </div>
                  <div className="form-group pb-4">
                    <label className="form-label"><strong>Password</strong></label>
                    <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required />
                  </div>
                  <div className="container-fluid text-center py-4">
                    <button type="submit" className="primary-btn" style={{width: '100%'}} disabled={loading || otpSending}>
                      {loading ? 'Sending...' : 'Send OTP'}
                    </button>
                  </div>
                </form>
              )}

              {step === 'otp' && (
                <form className="container" onSubmit={verifyOtpAndDeactivate}>
                  <div className="form-group pb-3">
                    <label className="form-label"><strong>Enter 6-digit OTP sent to {email}</strong></label>
                    <div className="otp-inputs d-flex justify-content-center gap-2 mb-3">
                      {otp.map((digit, index) => (
                        <input 
                          key={index}
                          type="text" 
                          maxLength="1" 
                          value={digit} 
                          ref={(el) => (inputs.current[index] = el)} 
                          onChange={(e) => handleOtpChange(e.target.value, index)}
                          className="form-control text-center fw-bold otp-box" 
                          style={{ width: '50px', height: '50px', fontSize: '18px' }} 
                          disabled={otpVerifying}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="container-fluid text-center py-4">
                    <button type="submit" className="primary-btn" style={{width: '100%'}} disabled={loading || otpVerifying}>
                      {otpVerifying || loading ? 'Deactivating...' : 'Deactivate Account'}
                    </button>
                  </div>
                  <div className="container-fluid text-center py-3">
                    <button type="button" className="btn btn-link" onClick={changeCredentials}>
                      Wrong email/password? Change
                    </button>
                  </div>
                </form>
              )}
              
              <div id="developer-section" className="container-fluid text-center py-3 mt-4 pt-4 border-top">
                <div className="developer-info small">
                  <strong>About the developer</strong><br/>
                  Precise Behavioural, Inc.<br/>
                  informatics@precisebh.com<br/>
                  2393 Townsgate Rd., Suite 104<br/>
                  Westlake Village, CA 91361-2513<br/>
                  United States<br/>
                  +1 805-601-5199
                </div>
              </div>
            </CardLayout>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeleteAccount;
